/**
 * Lab page component - route handler for individual labs
 * Supports deep linking: /labs/:labId/sub/path?query=1#hash
 */

import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { Alert, Box, Button } from "@mui/material";
import { Home } from "@mui/icons-material";
import { useLabsConfig, resolveLabUrl } from "../utils/useLabsConfig";
import { LabFrame } from "../components/lab/LabFrame";
import { PageLayoutDesktop } from "../components/layout/PageLayoutDesktop";
import { useCallback } from "react";

export function LabPage() {
  const { labId, "*": subPath } = useParams<{ labId: string; "*": string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const labs = useLabsConfig();

  const lab = labs.find((l) => l.id === labId);

  // Build the full iframe URL including sub-path, query string, and hash
  const buildIframeUrl = useCallback(() => {
    if (!lab) return "";

    const baseUrl = resolveLabUrl(lab.url);
    const queryPart = location.search || "";
    const hashPart = location.hash || "";

    // Handle slash between baseUrl and subPath to avoid double slashes
    let fullPath = baseUrl;
    if (subPath) {
      const baseEndsWithSlash = baseUrl.endsWith("/");
      const subStartsWithSlash = subPath.startsWith("/");

      if (baseEndsWithSlash && subStartsWithSlash) {
        // Both have slash - remove one
        fullPath = baseUrl + subPath.slice(1);
      } else if (!baseEndsWithSlash && !subStartsWithSlash) {
        // Neither has slash - add one
        fullPath = baseUrl + "/" + subPath;
      } else {
        // Exactly one has slash - just concatenate
        fullPath = baseUrl + subPath;
      }
    }

    return `${fullPath}${queryPart}${hashPart}`;
  }, [lab, subPath, location.search, location.hash]);

  // Handle iframe navigation - update parent URL to match
  const handleIframeNavigate = useCallback(
    (iframePath: string) => {
      if (!lab) return;

      // Extract the path portion that comes after the lab's base URL
      const baseUrl = resolveLabUrl(lab.url);

      // Parse the iframe's full URL to get path, search, and hash
      let relativePath = "";
      let search = "";
      let hash = "";

      try {
        // Handle both absolute and relative URLs
        const url = new URL(iframePath, window.location.origin);
        const basePath = new URL(baseUrl, window.location.origin).pathname;

        // Get the path after the base lab path
        if (url.pathname.startsWith(basePath)) {
          relativePath = url.pathname.slice(basePath.length);
        }
        search = url.search;
        hash = url.hash;
      } catch {
        // If URL parsing fails, just use the path as-is
        relativePath = iframePath;
      }

      // Ensure relativePath has leading slash if non-empty
      if (relativePath && !relativePath.startsWith("/")) {
        relativePath = "/" + relativePath;
      }

      // Build the new parent URL
      const newPath = `/labs/${labId}${relativePath}${search}${hash}`;

      // Only update if different from current location
      if (newPath !== `${location.pathname}${location.search}${location.hash}`) {
        // Use replace to avoid polluting history for minor navigation
        navigate(newPath, { replace: true });
      }
    },
    [lab, labId, location.pathname, location.search, location.hash, navigate]
  );

  if (!lab || !lab.url) {
    return (
      <PageLayoutDesktop
        content={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              gap: 2,
              p: 3,
            }}
          >
            <Alert severity="error">
              Lab not found: {labId}
              <br />
              The requested lab does not exist or is not enabled.
            </Alert>
            <Button variant="contained" startIcon={<Home />} component={Link} to="/">
              Go to Home
            </Button>
          </Box>
        }
      />
    );
  }

  const iframeUrl = buildIframeUrl();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "calc(100vh - 64px)", md: "100vh" },
        m: { xs: -1, md: -3 },
      }}
    >
      <LabFrame url={iframeUrl} title={lab.name} onNavigate={handleIframeNavigate} />
    </Box>
  );
}
